// Shared frame primitives for eligibility criterion cards — Figma 8301:73166 family.

import type { CSSProperties, ReactNode } from "react";

import { cn } from "@/lib/utils";

/** Figma card frame — 273×240 at 1152px content width. */
export const CRITERION_CARD_WIDTH = 273;
export const CRITERION_CARD_HEIGHT = 240;

/** How the artboard scales inside the tile. */
export type CriterionCardFit = "contain" | "cover";

/** Mock placement anchor — desktop Figma reference (273px card). */
export type CriterionMockAnchor = "bottom-left" | "center" | "top-left";

function criterionArtboardScale(fit: CriterionCardFit) {
  return fit === "cover"
    ? "scale(calc(100cqw / 273px))"
    : "scale(min(100cqw / 273px, 1))";
}

const TRANSFORM_ORIGIN: Record<CriterionMockAnchor, string> = {
  "bottom-left": "bottom left",
  center: "top center",
  "top-left": "top left",
};

/** Downscale bleed-heavy mocks from an anchor; never upscale past native size. */
export function criterionMockScaleStyle(
  naturalWidth: number,
  anchor: CriterionMockAnchor,
  /** Horizontal inset budget subtracted from 100cqw before scaling. */
  gutterPx = 0
): CSSProperties {
  const widthExpr = gutterPx > 0 ? `(100cqw - ${gutterPx}px)` : "100cqw";

  return {
    width: naturalWidth,
    transformOrigin: TRANSFORM_ORIGIN[anchor],
    transform: `scale(min(1, calc(${widthExpr} / ${naturalWidth}px)))`,
  };
}

function CriterionArtboardScaler({
  children,
  fit = "contain",
  overflow = "hidden",
  className,
}: {
  children: ReactNode;
  fit?: CriterionCardFit;
  overflow?: "hidden" | "visible";
  className?: string;
}) {
  return (
    <div className={cn("absolute inset-0 flex justify-center", className)}>
      <div
        className="shrink-0 origin-top"
        style={{
          width: CRITERION_CARD_WIDTH,
          height: CRITERION_CARD_HEIGHT,
          transform: criterionArtboardScale(fit),
        }}
      >
        <div
          className={cn(
            "relative h-[240px] w-[273px]",
            overflow === "hidden" ? "overflow-hidden" : "overflow-visible"
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/** @deprecated Use CriterionArtboardLayer — kept for card index backward compat. */
export function ScaledCriterionCanvas({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <CriterionArtboardScaler fit="contain" overflow="hidden">
      {children}
    </CriterionArtboardScaler>
  );
}

/** Tile-width layer that shares the 273×240 artboard coordinate space. */
export function CriterionArtboardLayer({
  children,
  fit = "contain",
  className,
}: {
  children: ReactNode;
  fit?: CriterionCardFit;
  className?: string;
}) {
  return (
    <CriterionArtboardScaler
      fit={fit}
      overflow="visible"
      className={cn("pointer-events-none", className)}
    >
      {children}
    </CriterionArtboardScaler>
  );
}

/** Figma card frame — 273×240 artboard scaled to the tile. */
export function CriterionCardFrame({
  children,
  className,
  fit = "contain",
  overlay,
  "data-testid": dataTestId,
}: {
  children: ReactNode;
  className?: string;
  /** contain: never upscale (default). cover: fill tile width like object-fit cover. */
  fit?: CriterionCardFit;
  /** Artboard-aligned layer for bleed-heavy mocks (same coords as title artboard). */
  overlay?: ReactNode;
  "data-testid"?: string;
}) {
  return (
    <article
      data-testid={dataTestId}
      className={cn(
        "border-grey-100 @container/eligibility-card relative h-[240px] overflow-hidden rounded-2xl border bg-white",
        className
      )}
    >
      {overlay ? (
        <CriterionArtboardLayer fit={fit}>{overlay}</CriterionArtboardLayer>
      ) : null}
      <CriterionArtboardScaler fit={fit} overflow="hidden">
        {children}
      </CriterionArtboardScaler>
    </article>
  );
}

export function CriterionCardTitle({
  lines,
  className,
}: {
  lines: Array<Array<{ text: string; muted?: boolean }>>;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "absolute top-[24px] left-[24px] text-[18px] leading-[1.3] tracking-[-0.36px]",
        className
      )}
    >
      {lines.map((segments, index) => (
        <p key={index}>
          {segments.map((segment) => (
            <span
              key={segment.text}
              className={segment.muted ? "text-[#7b8084]" : "text-[#0c0d0d]"}
            >
              {segment.text}
            </span>
          ))}
        </p>
      ))}
    </div>
  );
}

export function RegionPill({
  flag,
  label,
  className,
  "data-testid": dataTestId,
}: {
  flag: string;
  label: string;
  className?: string;
  "data-testid"?: string;
}) {
  return (
    <div
      data-testid={dataTestId}
      className={cn(
        "absolute flex items-center gap-[7.234px] rounded-full bg-white py-[7.989px] pr-[14.468px] pl-[7.989px] shadow-[0px_4.827px_12.067px_rgba(0,0,0,0.12)]",
        className
      )}
    >
      <span
        className="flex size-[24.019px] shrink-0 items-center justify-center text-[16px] leading-none"
        aria-hidden
      >
        {flag}
      </span>
      <span className="text-[12px] leading-[1.4] tracking-[-0.24px] whitespace-nowrap text-[#313335]">
        {label}
      </span>
    </div>
  );
}

export function IndustryPill({
  iconSrc,
  iconBg,
  iconBorder,
  label,
  labelClassName,
  className,
}: {
  iconSrc: string;
  iconBg: string;
  iconBorder: string;
  label: string;
  labelClassName?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center gap-[7.234px] rounded-full bg-white py-[7.989px] pr-[14.468px] pl-[7.989px] shadow-[0px_4.827px_12.067px_rgba(0,0,0,0.12)]",
        className
      )}
    >
      <span
        className={cn(
          "flex size-[21.304px] shrink-0 items-center justify-center overflow-hidden rounded-full border-[1.331px]",
          iconBg,
          iconBorder
        )}
      >
        <img src={iconSrc} alt="" className="size-[12px]" aria-hidden />
      </span>
      <span
        className={cn(
          "text-[15.978px] leading-[1.2] tracking-[-0.639px] whitespace-nowrap",
          labelClassName
        )}
      >
        {label}
      </span>
    </div>
  );
}
